import { useState, type FormEvent } from 'react';
import type { ServiceRate } from '@stfors/shared-types';
import {
  useServiceRatesAdmin,
  useCreateServiceRate,
  useUpdateServiceRate,
  useDeleteServiceRate,
  useCopyRatesToGlobal,
  useEngagementRateCandidates,
  useImportEngagementRates,
  useMergeRates,
  type ServiceRatesAdminFilters,
} from '../api/queries/service-rates';
import { useClients } from '../api/queries/clients';
import { useProjects } from '../api/queries/projects';
import { Modal } from '../components/Modal';
import { IconAction } from '../components/IconAction';
import { MoneyInput } from '../components/MoneyInput';
import { ProjectOptions } from '../components/ProjectOptions';
import { partitionByClosed } from '../utils/project';
import { ColumnSettings } from '../components/ColumnSettings';
import { useColVisibility, type ColumnDef } from '../hooks/useColVisibility';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import './ServiceRatesPage.css';

type RateSortKey = 'name' | 'type' | 'amount' | 'discount' | 'description' | 'source' | 'status';

// Menu order = table order here. Name and Amount are locked: a rate sheet
// without them isn't one. The trailing actions column has no header and no
// entry — it isn't a data column.
//
// Nothing is hidden by default. Discount is the reason this menu exists (it is
// a negotiation detail you may not want on screen), but it is also the point of
// the client/project tiers, so hiding it for everyone would be the wrong guess.
const RATE_COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'Name', locked: true },
  { key: 'type', label: 'Type' },
  { key: 'amount', label: 'Amount', locked: true },
  { key: 'discount', label: 'Discount' },
  { key: 'description', label: 'Description' },
  { key: 'source', label: 'Source' },
  { key: 'status', label: 'Status' },
];

type ScopeKind = 'global' | 'client' | 'project';
type Source = 'global' | 'client' | 'project';

interface SheetRow {
  row: ServiceRate;
  source: Source;
  // True when the effective row is an override stored AT the selected scope
  // (vs. inherited from a less-specific level).
  overriddenHere: boolean;
  // % below the Global rate of the same name+type, or null (global rows, no
  // Global counterpart, type mismatch, or not actually cheaper).
  discountPct: number | null;
}

import { formatMoney, discountPct } from '../utils/money';
import { Toggle } from '../components/Toggle';
import { apiErrorText } from '../utils/apiErrors';

// Collapse the API's ranked rows (most-specific-first) into the effective
// sheet for the selected scope: one row per name, labelled with its source.
function computeSheet(
  rows: ServiceRate[],
  kind: ScopeKind,
  clientId: number | null,
  projectId: number | null,
): SheetRow[] {
  let out: SheetRow[];
  if (kind === 'global') {
    out = rows
      .filter((r) => r.client_override_for == null && r.project_override_for == null)
      .map((r) => ({ row: r, source: 'global' as const, overriddenHere: true, discountPct: null }));
  } else {
    // The Global tier is in the same response (both override cols null); use it
    // as the baseline to derive each override's discount.
    const globalByName = new Map<string, ServiceRate>();
    for (const r of rows) {
      if (r.client_override_for == null && r.project_override_for == null) globalByName.set(r.name, r);
    }
    const seen = new Set<string>();
    out = [];
    for (const r of rows) {
      if (seen.has(r.name)) continue;
      seen.add(r.name);
      const source: Source =
        r.project_override_for != null ? 'project'
        : r.client_override_for != null ? 'client'
        : 'global';
      const overriddenHere =
        kind === 'project'
          ? r.project_override_for === projectId
          : r.client_override_for === clientId;
      const g = source === 'global' ? undefined : globalByName.get(r.name);
      const dp = g && g.rate_type === r.rate_type ? discountPct(r.amount, g.amount) : null;
      out.push({ row: r, source, overriddenHere, discountPct: dp });
    }
  }
  return out.sort((a, b) => a.row.name.localeCompare(b.row.name));
}

export function ServiceRatesPage(): JSX.Element {
  const canEdit = can('service_rate.write');

  // ---- Scope picker state ----
  const [scopeKind, setScopeKind] = useState<ScopeKind>('global');
  const [scopeClientId, setScopeClientId] = useState<number | ''>('');
  const [scopeProjectId, setScopeProjectId] = useState<number | ''>('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const clientsQuery = useClients();
  const projectsQuery = useProjects();
  const clients = clientsQuery.data?.clients ?? [];
  const projects = projectsQuery.data?.projects ?? [];
  // "Show inactive" governs BOTH inactive rates and closed matters — one
  // toggle on the page, one meaning. A closed case's rate sheet is history you
  // occasionally need, not something to scroll past on every visit.
  //
  // A project already CHOSEN as the scope stays listed whatever the toggle
  // says: dropping it would blank the control while its sheet is still on
  // screen below.
  const projectsForScope = (selected: number | ''): typeof projects => {
    if (includeInactive) return projects;
    const { active } = partitionByClosed(projects);
    if (selected === '' || active.some((p) => p.id === selected)) return active;
    const kept = projects.find((p) => p.id === selected);
    return kept ? [...active, kept] : active;
  };

  const clientId = scopeKind === 'client' && scopeClientId !== '' ? scopeClientId : null;
  const projectId = scopeKind === 'project' && scopeProjectId !== '' ? scopeProjectId : null;
  const scopeReady = scopeKind === 'global' || clientId !== null || projectId !== null;

  const filters: ServiceRatesAdminFilters = { include_inactive: includeInactive };
  if (clientId !== null) filters.client_id = clientId;
  if (projectId !== null) filters.project_id = projectId;
  const { data, isPending, error } = useServiceRatesAdmin(filters);

  // ---- Mutations ----
  const createRate = useCreateServiceRate();
  const updateRate = useUpdateServiceRate();
  const deleteRate = useDeleteServiceRate();
  const copyRates = useCopyRatesToGlobal();
  const importEngagement = useImportEngagementRates();
  const mergeRates = useMergeRates();

  // Engagement-letter rate sheet available to import for the selected project.
  const engagementCandidatesQuery = useEngagementRateCandidates(projectId ?? undefined);
  const engagementCandidates = engagementCandidatesQuery.data?.candidates ?? [];

  // ---- New-rate form state ----
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'hourly' | 'flat_fee'>('hourly');
  // Product sale (0059): entries record a sale date + the device sold, no time.
  const [newProductSale, setNewProductSale] = useState(false);
  const [saleError, setSaleError] = useState<string | null>(null);
  const [newAmount, setNewAmount] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newScopeKind, setNewScopeKind] = useState<ScopeKind>('global');
  const [newScopeClientId, setNewScopeClientId] = useState<number | ''>('');
  const [newScopeProjectId, setNewScopeProjectId] = useState<number | ''>('');
  const [createError, setCreateError] = useState<string | null>(null);

  // ---- Inline edit state (a global row's name/amount/desc) ----
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // ---- Override state (creating/editing a scoped amount) ----
  const [overrideName, setOverrideName] = useState<string | null>(null);
  const [overrideAmount, setOverrideAmount] = useState('');

  // ---- Copy-to-global state ----
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [copyResult, setCopyResult] = useState<string | null>(null);

  // ---- Import-from-engagement state ----
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  // ---- Drag-to-merge state ----
  const [dragSource, setDragSource] = useState<SheetRow | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [mergeFrom, setMergeFrom] = useState<SheetRow | null>(null);
  const [mergeTo, setMergeTo] = useState<SheetRow | null>(null);
  const [mergeChoice, setMergeChoice] = useState<'source' | 'target' | 'custom'>('source');
  const [mergeCustom, setMergeCustom] = useState('');
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeResult, setMergeResult] = useState<string | null>(null);

  // Whether picking the target's amount means "inherit" (the target is a less
  // specific level than where the source lives) vs. just dedupe at the same level.
  const mergeTargetInherited = mergeTo ? !mergeTo.overriddenHere : false;

  const openMerge = (from: SheetRow, to: SheetRow) => {
    setMergeFrom(from);
    setMergeTo(to);
    setMergeChoice('source');
    setMergeCustom(from.row.amount);
    setMergeError(null);
  };

  const doMerge = () => {
    if (!mergeFrom || !mergeTo) return;
    setMergeError(null);
    let amount: string | null;
    if (mergeChoice === 'source') {
      amount = mergeFrom.row.amount;
    } else if (mergeChoice === 'target') {
      // Picking the target's amount when it's inherited == "just inherit it".
      amount = mergeTargetInherited ? null : mergeTo.row.amount;
    } else {
      if (!/^\d+(\.\d{1,2})?$/.test(mergeCustom.trim())) {
        setMergeError('Amount format invalid (e.g. 275.00).');
        return;
      }
      amount = mergeCustom.trim();
    }
    const fromName = mergeFrom.row.name;
    const toName = mergeTo.row.name;
    mergeRates.mutate(
      { source_id: mergeFrom.row.id, target_name: toName, amount },
      {
        onSuccess: () => {
          setMergeResult(`Merged "${fromName}" into "${toName}".`);
          setMergeFrom(null);
          setMergeTo(null);
        },
        onError: () => setMergeError('Could not merge. Try again.'),
      },
    );
  };

  const openNew = () => {
    setNewScopeKind(scopeKind);
    setNewScopeClientId(scopeClientId);
    setNewScopeProjectId(scopeProjectId);
    setNewName('');
    setNewAmount('');
    setNewDescription('');
    setNewType('hourly');
    setNewProductSale(false);
    setCreateError(null);
    setShowNew(true);
  };

  const startEditGlobal = (r: ServiceRate) => {
    setOverrideName(null);
    setEditingId(r.id);
    setEditName(r.name);
    setEditAmount(r.amount);
    setEditDescription(r.description ?? '');
  };

  const saveEditGlobal = (id: number) => {
    updateRate.mutate(
      {
        id,
        name: editName.trim(),
        amount: editAmount.trim(),
        description: editDescription.trim() || null,
      },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const startOverride = (r: ServiceRate) => {
    setEditingId(null);
    setOverrideName(r.name);
    setOverrideAmount(r.amount);
  };

  // Persist an override: PATCH when one already exists here, else POST a new
  // scoped rate carrying the parent rate's identity (name/type/description).
  const saveOverride = (sheet: SheetRow) => {
    if (!/^\d+(\.\d{1,2})?$/.test(overrideAmount.trim())) return;
    if (sheet.overriddenHere) {
      updateRate.mutate(
        { id: sheet.row.id, amount: overrideAmount.trim() },
        { onSuccess: () => setOverrideName(null) },
      );
    } else {
      createRate.mutate(
        {
          name: sheet.row.name,
          rate_type: sheet.row.rate_type,
          amount: overrideAmount.trim(),
          description: sheet.row.description ?? undefined,
          client_override_for: scopeKind === 'client' ? clientId : null,
          project_override_for: scopeKind === 'project' ? projectId : null,
        },
        { onSuccess: () => setOverrideName(null) },
      );
    }
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newName.trim().length === 0 || newAmount.trim().length === 0) return;
    if (!/^\d+(\.\d{1,2})?$/.test(newAmount)) {
      setCreateError('Amount format invalid (e.g. 275.00).');
      return;
    }
    if (newScopeKind === 'client' && newScopeClientId === '') {
      setCreateError('Pick a client for this client-specific rate.');
      return;
    }
    if (newScopeKind === 'project' && newScopeProjectId === '') {
      setCreateError('Pick a project for this project-specific rate.');
      return;
    }
    try {
      await createRate.mutateAsync({
        name: newName.trim(),
        rate_type: newType,
        amount: newAmount.trim(),
        description: newDescription.trim() || undefined,
        client_override_for: newScopeKind === 'client' ? Number(newScopeClientId) : null,
        project_override_for: newScopeKind === 'project' ? Number(newScopeProjectId) : null,
        // Only a flat fee can be a product sale; the API refuses it otherwise.
        is_product_sale: newType === 'flat_fee' && newProductSale,
      });
      setShowNew(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to create service rates.");
        return;
      }
      setCreateError(apiErrorText(err, 'Could not save.'));
    }
  };

  const doCopyToGlobal = () => {
    setCopyResult(null);
    copyRates.mutate(
      scopeKind === 'project' ? { from_project_id: projectId! } : { from_client_id: clientId! },
      {
        onSuccess: (res) => {
          setShowCopyConfirm(false);
          setCopyResult(
            `Global rates updated to match this scope — ${res.created} created, ${res.updated} updated.`,
          );
        },
      },
    );
  };

  const doImportFromEngagement = () => {
    if (projectId === null) return;
    setImportResult(null);
    importEngagement.mutate(projectId, {
      onSuccess: (res) => {
        setShowImportConfirm(false);
        setImportResult(
          `Imported the engagement letter's rate sheet — ${res.created} created, ${res.updated} updated.`,
        );
      },
    });
  };

  const scopeNounLabel = (() => {
    if (scopeKind === 'client') {
      const c = clients.find((cl) => cl.id === clientId);
      return c ? c.name : 'this client';
    }
    if (scopeKind === 'project') {
      const p = projects.find((pr) => pr.id === projectId);
      return p ? `${p.case_number} — ${p.title}` : 'this project';
    }
    return 'Global';
  })();

  // Name and Amount are locked: a rate sheet with neither is not a rate sheet,
  // and the actions column has no header to hide behind.
  const vis = useColVisibility('service-rates', RATE_COLUMNS);

  const sheet =
    scopeReady && data ? computeSheet(data.service_rates, scopeKind, clientId, projectId) : [];
  const sort = useClientSort(sheet, (s, key: RateSortKey) => {
    switch (key) {
      case 'name': return s.row.name;
      case 'type': return s.row.rate_type;
      case 'amount': return Number(s.row.amount);
      case 'discount': return s.discountPct;
      case 'description': return s.row.description ?? '';
      case 'source': return s.source;
      case 'status': return s.row.is_active ? 1 : 0;
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  return (
    <section className="page-stack page-stack-wide">
      <div className="card sr-toolbar">
        <header className="sr-header">
          <h2>Service Rates</h2>
          <div className="sr-header-controls">
            <Toggle
              checked={includeInactive}
              onChange={setIncludeInactive}
              label="Show inactive"
              title="Include inactive rates and closed matters"
            />
            {canEdit && !showNew && (
              <button type="button" className="btn-cyan-primary" onClick={openNew}>
                New rate
              </button>
            )}
          </div>
        </header>
      </div>

      {/* Scope picker */}
      <div className="card">
        <div className="sr-scope-bar">
          <span className="sr-scope-label">View</span>
          <div className="sr-scope-seg" role="group" aria-label="Rate scope">
            {(['global', 'client', 'project'] as ScopeKind[]).map((k) => (
              <button
                key={k}
                type="button"
                className={scopeKind === k ? 'active' : ''}
                onClick={() => setScopeKind(k)}
              >
                {k === 'global' ? 'Global' : k === 'client' ? 'Client' : 'Project'}
              </button>
            ))}
          </div>

          {scopeKind === 'client' && (
            <select
              className="form-input sr-scope-select"
              value={scopeClientId}
              onChange={(e) => setScopeClientId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          {scopeKind === 'project' && (
            <select
              className="form-input sr-scope-select"
              value={scopeProjectId}
              onChange={(e) => setScopeProjectId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">Select a project…</option>
              <ProjectOptions projects={projectsForScope(scopeProjectId)} />
            </select>
          )}

          <span className="sr-scope-spacer" />

          {canEdit && scopeKind === 'project' && projectId !== null && engagementCandidates.length > 0 && (
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => setShowImportConfirm(true)}
            >
              Import from engagement letter ({engagementCandidates.length})
            </button>
          )}

          {canEdit && scopeKind !== 'global' && scopeReady && (
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => setShowCopyConfirm(true)}
              disabled={sheet.length === 0}
            >
              Copy these rates to Global
            </button>
          )}
        </div>
      </div>

      {copyResult && (
        <p className="sr-info-banner" role="status">
          {copyResult}
        </p>
      )}

      {importResult && (
        <p className="sr-info-banner" role="status">
          {importResult}
        </p>
      )}

      {mergeResult && (
        <p className="sr-info-banner" role="status">
          {mergeResult}
        </p>
      )}

      {showNew && (
        <div className="card">
          <form className="sr-new-form" onSubmit={onCreate}>
            <div className="sr-new-row">
              <label className="sr-field">
                <span>Name *</span>
                <input
                  type="text"
                  className="form-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={200}
                  required
                  disabled={createRate.isPending}
                />
              </label>
              <label className="sr-field">
                <span>Type *</span>
                <select
                  className="form-input"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as 'hourly' | 'flat_fee')}
                  disabled={createRate.isPending}
                >
                  <option value="hourly">Hourly</option>
                  <option value="flat_fee">Flat fee</option>
                </select>
              </label>
              <label className="sr-field">
                <span>Amount (USD) *</span>
                <MoneyInput
                  value={newAmount}
                  onChange={setNewAmount}
                  placeholder="275.00"
                  required
                  disabled={createRate.isPending}
                />
              </label>
            </div>
            <label className="sr-field sr-field-wide">
              <span>Description</span>
              <input
                type="text"
                className="form-input"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                maxLength={2000}
                disabled={createRate.isPending}
              />
            </label>
            <label className="sr-field sr-field-wide">
              <span>Scope</span>
              <select
                className="form-input"
                value={newScopeKind}
                onChange={(e) => setNewScopeKind(e.target.value as ScopeKind)}
                disabled={createRate.isPending}
              >
                <option value="global">Global — the default for everyone</option>
                <option value="client">Client-specific override</option>
                <option value="project">Project-specific override</option>
              </select>
            </label>
            {newScopeKind === 'client' && (
              <label className="sr-field sr-field-wide">
                <span>Client *</span>
                <select
                  className="form-input"
                  value={newScopeClientId}
                  onChange={(e) =>
                    setNewScopeClientId(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  disabled={createRate.isPending}
                >
                  <option value="">Select a client…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {newScopeKind === 'project' && (
              <label className="sr-field sr-field-wide">
                <span>Project *</span>
                <select
                  className="form-input"
                  value={newScopeProjectId}
                  onChange={(e) =>
                    setNewScopeProjectId(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  disabled={createRate.isPending}
                >
                  <option value="">Select a project…</option>
                  <ProjectOptions projects={projectsForScope(newScopeProjectId)} />
                </select>
              </label>
            )}
            {newType === 'flat_fee' && (
              <Toggle
                checked={newProductSale}
                onChange={setNewProductSale}
                disabled={createRate.isPending}
                label="Product sale"
                description="A device sold to the client (e.g. production media): Log Time asks for a sale date and the device sold — no time or hours."
              />
            )}
            {createError && (
              <p className="sr-error-banner" role="alert">
                {createError}
              </p>
            )}
            <div className="sr-new-actions">
              <button
                type="button"
                className="btn-charcoal-secondary"
                onClick={() => {
                  setShowNew(false);
                  setCreateError(null);
                }}
                disabled={createRate.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-cyan-primary"
                disabled={
                  createRate.isPending ||
                  newName.trim().length === 0 ||
                  newAmount.trim().length === 0
                }
              >
                {createRate.isPending ? 'Saving…' : 'Save rate'}
              </button>
            </div>
          </form>
        </div>
      )}

      {isPending && scopeReady ? (
        <p className="sr-status">Loading rates…</p>
      ) : error ? (
        <p className="sr-status sr-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view service rates."
            : 'Failed to load.'}
        </p>
      ) : !scopeReady ? (
        <div className="card">
          <div className="sr-empty">
            <p>Select a {scopeKind} to view its rate sheet.</p>
          </div>
        </div>
      ) : sheet.length === 0 ? (
        <div className="card">
          <div className="sr-empty">
            <p>No service rates to show.</p>
            {canEdit && <p className="sr-empty-meta">Click "New rate" to add one.</p>}
          </div>
        </div>
      ) : (
        <div className="card card-tight sr-table-card table-scroll-card">
          <div className="sr-table-tools">
            <ColumnSettings vis={vis} />
          </div>
          {saleError && (
            <p className="sr-error-banner" role="alert">{saleError}</p>
          )}
          <TableScroll id="service-rates">
          <table className="sr-table">
            <thead>
              <tr>
                <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                {vis.isVisible('type') && <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                <SortableTh column="amount" label="Amount" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="sr-numeric" />
                {vis.isVisible('discount') && <SortableTh column="discount" label="Discount" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="sr-numeric" />}
                {vis.isVisible('description') && <SortableTh column="description" label="Description" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('source') && <SortableTh column="source" label="Source" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('status') && <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((s) => {
                const r = s.row;
                const isGlobalEditing = editingId === r.id;
                const isOverriding = overrideName === r.name;
                // Only rows you own at this scope (●) can be dragged; any other
                // row is a drop target. Dragging is suspended while editing.
                const isDraggable =
                  canEdit && s.overriddenHere && editingId === null && overrideName === null;
                const isDropTarget = dragSource !== null && dragSource.row.id !== r.id;
                return (
                  <tr
                    key={`${s.source}-${r.id}`}
                    className={
                      (r.is_active ? '' : 'sr-row-inactive') +
                      (dragOverId === r.id ? ' sr-row-drag-over' : '') +
                      (isDraggable ? ' sr-row-draggable' : '')
                    }
                    draggable={isDraggable}
                    onDragStart={() => setDragSource(s)}
                    onDragEnd={() => {
                      setDragSource(null);
                      setDragOverId(null);
                    }}
                    onDragOver={(e) => {
                      if (isDropTarget) {
                        e.preventDefault();
                        setDragOverId(r.id);
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverId === r.id) setDragOverId(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragSource && dragSource.row.id !== r.id) openMerge(dragSource, s);
                      setDragSource(null);
                      setDragOverId(null);
                    }}
                    title={isDraggable ? 'Drag onto another rate to merge them' : undefined}
                  >
                    <td>
                      {scopeKind === 'global' && isGlobalEditing ? (
                        <input
                          type="text"
                          className="form-input sr-edit-input"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          disabled={updateRate.isPending}
                        />
                      ) : (
                        r.name
                      )}
                    </td>
                    {vis.isVisible('type') && (
                      <td>
                        {r.rate_type === 'hourly' ? 'Hourly' : 'Flat fee'}
                        {/* Product sale (0059) — set per SERVICE: flipping it on
                            any tier flips every tier of this name. */}
                        {r.rate_type === 'flat_fee' && (canEdit ? (
                          <Toggle
                            className="sr-sale-toggle"
                            size="sm"
                            checked={r.is_product_sale}
                            onChange={(v) => {
                              setSaleError(null);
                              updateRate.mutate(
                                { id: r.id, is_product_sale: v },
                                { onError: (err) => setSaleError(apiErrorText(err, 'Could not change the product-sale setting.')) },
                              );
                            }}
                            disabled={updateRate.isPending}
                            label="Product sale"
                            title="Entries on this rate record a sale date and the device sold — no time or hours. Applies to every tier of this rate."
                          />
                        ) : r.is_product_sale && (
                          <div className="sr-sale-tag">Product sale</div>
                        ))}
                      </td>
                    )}
                    <td className="sr-numeric sr-amount">
                      {isGlobalEditing ? (
                        <MoneyInput
                          className="sr-edit-input sr-edit-num"
                          value={editAmount}
                          onChange={setEditAmount}
                          disabled={updateRate.isPending}
                        />
                      ) : isOverriding ? (
                        <MoneyInput
                          className="sr-edit-input sr-edit-num"
                          value={overrideAmount}
                          onChange={setOverrideAmount}
                          disabled={createRate.isPending || updateRate.isPending}
                        />
                      ) : (
                        formatMoney(r.amount)
                      )}
                    </td>
                    {vis.isVisible('discount') && (
                      <td className="sr-numeric">{s.discountPct != null ? `${s.discountPct}%` : '—'}</td>
                    )}
                    {vis.isVisible('description') && (
                      <td className="sr-desc">
                        {scopeKind === 'global' && isGlobalEditing ? (
                          <input
                            type="text"
                            className="form-input sr-edit-input"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            disabled={updateRate.isPending}
                          />
                        ) : (
                          r.description ?? ''
                        )}
                      </td>
                    )}
                    {vis.isVisible('source') && (
                      <td>
                        {s.source === 'project' ? (
                          <span className="sr-chip sr-chip-cyan">Project</span>
                        ) : s.source === 'client' ? (
                          <span className="sr-chip sr-chip-muted">Client</span>
                        ) : (
                          <span className="sr-global">Global</span>
                        )}
                      </td>
                    )}
                    {vis.isVisible('status') && (
                      <td>
                        <span className={r.is_active ? 'sr-pill sr-pill-active' : 'sr-pill sr-pill-inactive'}>
                          {r.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    )}
                    <td className="sr-actions">
                      {canEdit &&
                        (scopeKind === 'global' ? (
                          isGlobalEditing ? (
                            <>
                              <IconAction
                                action="save"
                                onClick={() => saveEditGlobal(r.id)}
                                disabled={updateRate.isPending}
                                iconSize={16}
                              />
                              <IconAction
                                action="cancel"
                                onClick={() => setEditingId(null)}
                                disabled={updateRate.isPending}
                                iconSize={16}
                              />
                            </>
                          ) : (
                            <>
                              <IconAction
                                action="edit"
                                onClick={() => startEditGlobal(r)}
                                iconSize={16}
                              />
                              <IconAction
                                action={r.is_active ? 'pause' : 'resume'}
                                label={r.is_active ? 'Deactivate' : 'Reactivate'}
                                onClick={() => updateRate.mutate({ id: r.id, is_active: !r.is_active })}
                                disabled={updateRate.isPending}
                                iconSize={16}
                              />
                            </>
                          )
                        ) : isOverriding ? (
                          <>
                            <IconAction
                              action="save"
                              onClick={() => saveOverride(s)}
                              disabled={createRate.isPending || updateRate.isPending}
                              iconSize={16}
                            />
                            <IconAction
                              action="cancel"
                              onClick={() => setOverrideName(null)}
                              disabled={createRate.isPending || updateRate.isPending}
                              iconSize={16}
                            />
                          </>
                        ) : s.overriddenHere ? (
                          <>
                            <IconAction
                              action="edit"
                              onClick={() => startOverride(r)}
                              iconSize={16}
                            />
                            <IconAction
                              action="delete"
                              label="Clear override"
                              onClick={() => deleteRate.mutate(r.id)}
                              disabled={deleteRate.isPending}
                              iconSize={16}
                            />
                          </>
                        ) : (
                          <button type="button" className="sr-link sr-link-cyan" onClick={() => startOverride(r)}>
                            override here
                          </button>
                        ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
              </TableScroll>
        </div>
      )}

      <Modal
        title="Copy These Rates to Global"
        open={showCopyConfirm}
        onClose={() => setShowCopyConfirm(false)}
        busy={copyRates.isPending}
        footer={
          <>
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => setShowCopyConfirm(false)}
              disabled={copyRates.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={doCopyToGlobal}
              disabled={copyRates.isPending}
            >
              {copyRates.isPending ? 'Copying…' : 'Overwrite Global'}
            </button>
          </>
        }
      >
        <p>
          This sets the <strong>Global</strong> default rates to match the effective sheet for{' '}
          <strong>{scopeNounLabel}</strong> — existing global rates with the same name are
          overwritten, and any missing ones are created. Per-client and per-project overrides are
          left untouched.
        </p>
      </Modal>

      <Modal
        title="Import from Engagement Letter"
        open={showImportConfirm}
        onClose={() => setShowImportConfirm(false)}
        busy={importEngagement.isPending}
        footer={
          <>
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => setShowImportConfirm(false)}
              disabled={importEngagement.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={doImportFromEngagement}
              disabled={importEngagement.isPending}
            >
              {importEngagement.isPending ? 'Importing…' : `Import ${engagementCandidates.length} rates`}
            </button>
          </>
        }
      >
        <p>
          These rates were parsed from the engagement letter for <strong>{scopeNounLabel}</strong>.
          Importing creates them as <strong>project</strong> overrides (re-importing updates them).
          Engagement units that aren't hourly become a flat fee — the original unit is kept in the
          description.
        </p>
        <table className="sr-table">
          <thead>
            <tr>
              <th>Rate</th>
              <th>From letter</th>
              <th>As</th>
              <th className="sr-numeric">Amount</th>
            </tr>
          </thead>
          <tbody>
            {engagementCandidates.map((c, i) => (
              <tr key={i}>
                <td>{c.name}</td>
                <td className="sr-desc">
                  {c.unit ?? '—'}
                  {c.amount_qualifier ?? ''}
                </td>
                <td>{c.rate_type === 'hourly' ? 'Hourly' : 'Flat fee'}</td>
                <td className="sr-numeric sr-amount">{formatMoney(c.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>

      <Modal
        title="Merge Rates"
        open={mergeFrom !== null && mergeTo !== null}
        onClose={() => {
          setMergeFrom(null);
          setMergeTo(null);
        }}
        busy={mergeRates.isPending}
        footer={
          <>
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setMergeFrom(null);
                setMergeTo(null);
              }}
              disabled={mergeRates.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={doMerge}
              disabled={mergeRates.isPending}
            >
              {mergeRates.isPending ? 'Merging…' : 'Merge'}
            </button>
          </>
        }
      >
        {mergeFrom && mergeTo && (
          <>
            <p>
              Merge <strong>{mergeFrom.row.name}</strong> into{' '}
              <strong>{mergeTo.row.name}</strong>? The first name goes away and the rate keeps the
              amount you choose.
            </p>
            <div className="sr-merge-choices">
              <label className="sr-merge-choice">
                <input
                  type="radio"
                  name="merge-amount"
                  checked={mergeChoice === 'source'}
                  onChange={() => setMergeChoice('source')}
                />
                <span>
                  Keep {mergeFrom.row.name}&rsquo;s amount — {formatMoney(mergeFrom.row.amount)}
                </span>
              </label>
              <label className="sr-merge-choice">
                <input
                  type="radio"
                  name="merge-amount"
                  checked={mergeChoice === 'target'}
                  onChange={() => setMergeChoice('target')}
                />
                <span>
                  Use {mergeTo.row.name}&rsquo;s amount — {formatMoney(mergeTo.row.amount)}
                  {mergeTargetInherited ? ' (inherit — removes the override)' : ''}
                </span>
              </label>
              <label className="sr-merge-choice">
                <input
                  type="radio"
                  name="merge-amount"
                  checked={mergeChoice === 'custom'}
                  onChange={() => setMergeChoice('custom')}
                />
                <span>Custom:</span>
                <MoneyInput
                  className="sr-edit-input sr-edit-num"
                  value={mergeCustom}
                  onChange={(next) => {
                    setMergeCustom(next);
                    setMergeChoice('custom');
                  }}
                  disabled={mergeRates.isPending}
                />
              </label>
            </div>
            {mergeError && (
              <p className="sr-error-banner" role="alert">
                {mergeError}
              </p>
            )}
          </>
        )}
      </Modal>
    </section>
  );
}
